import React, { useState, useRef, useEffect, useContext } from 'react';
import {
    drawThinningTrail,
    drawPointer,
    getImageRect,
    fitCanvasToParent,
    observeSize
} from './CanvasUtil';
import TrailPublisher from './TrailPublisher';
import MobileUtil from '../../Util/MobileUtil';
import UserContext from '../../App/Contexts/UserContext';
import EventManager from '../../Util/EventManager';

/* Mouse trail adapted from a jQuery Codepen by Bryan C https://codepen.io/bryjch/pen/QEoXwA */
/* https://github.com/Egrodo/noahyamamoto.com/tree/master/src */

const MS_PER_FRAME = 16; // 16ms per frame at 60fps, matches the FPS constant in startAnimation

// A point is drawable only if it projected to real canvas coordinates: the
// terminal point that ends a trail carries no rx/ry, so its x/y come out NaN.
// 0 is a legitimate coordinate, so this cannot be a truthiness test. The null
// check is belt-and-braces - callers below always hold a point - and keeps
// this safe to reuse.
const hasPosition = (point) =>
    Boolean(point) && Number.isFinite(point.x) && Number.isFinite(point.y);

class Point {
    constructor(rx, ry, imageRect, terminal, t, renderAt) {
        // Keep the relative coordinates: they survive a resize, the absolute
        // ones don't
        this.rx = rx;
        this.ry = ry;
        this.project(imageRect);
        this.maxLife = 100;
        this.remainingLife = 100; // aged when moved to showing, based on t
        this.terminal = terminal; // boolean tracking if a user has submitted an "ending" point
        this.t = t;               // original capture timestamp for fade calculation
        this.renderAt = renderAt ?? 0; // scheduled render time (0 = render immediately)
    }

    // De-normalize relative coordinates to absolute canvas position
    // by scaling to image dimensions and adding the centering offset
    project(imageRect) {
        this.x = this.rx * imageRect.width + imageRect.x;
        this.y = this.ry * imageRect.height + imageRect.y;
    }

    decrement() {
        this.remainingLife -= 1;
    }

    percentage() {
        return (100 - this.remainingLife) / 100;
    }
}

class HardPoint {
    constructor(x, y) {
        this.x = x;
        this.y = y;
        this.maxLife = 100;
        this.remainingLife = 100;
        this.terminal = true; // boolean tracking if a user has submitted an "ending" point
    }

    decrement() {
        this.remainingLife -= 1;
    }

    percentage() {
        return (100 - this.remainingLife) / 100;
    }
}

function Canvas(props) {
    const img = props.img;
    const user = useContext(UserContext);
    // This uniqueDrawingId is used to allow your stuff to be drawn on another window, but not double drawn on this instance
    const [uniqueDrawingId] = useState(
        `${user.userId}_${Math.random()
            .toString(36)
            .replace(/[^a-z]+/g, '')
            .substr(0, 5)}_${user.fullName}`
    );
    const canRef = useRef(); // canvas
    const ctxRef = useRef(); // canvas 2d context
    // The mouse and path-event listeners are wired up once, in an effect keyed
    // on [canRef, props.readOnly], so a listener that closed over `img` would
    // hold whatever `img` was at mount - and at mount the slide image has not
    // loaded yet, so that is always null. getImageRect(null) spans the whole
    // canvas, which silently turned every coordinate canvas-relative instead
    // of image-relative. Read the current image through a ref instead.
    const imgRef = useRef(img);
    imgRef.current = img;
    const timeRef = useRef(0); // to track framerate
    const buttonRef = useRef(false); // is space bar pressed
    const animationRef = useRef(); // animationRequestFrame integer
    const trailPublisher = useRef();
    const streamingIntervalRef = useRef(null);
    // need to store image ref because animation loop 'remembers' old props and will render old props
    const pathRef = useRef({
        [uniqueDrawingId]: {
            showing: [],
            pending: []
        }
    });

    // The publisher buffers points between flushes, so replacing it drops
    // everything not yet sent - mid-stroke that silently truncates the trail
    // everyone else sees. roommateIds is rebuilt by refreshRoom() on every room
    // event, so keying this on it rebuilt the publisher whenever somebody
    // joined or left. Build it once and keep its recipients current instead.
    useEffect(() => {
        if (props.readOnly) return;

        if (trailPublisher.current) {
            trailPublisher.current.retarget(user, props.roommateIds, props.roomId);
        } else {
            trailPublisher.current = new TrailPublisher(
                user,
                props.roommateIds,
                props.roomId,
                uniqueDrawingId
            );
        }
    });

    const addRelativePoint = (user, relativePoint = {}) => {
        // If this user is brand new OR if they start a new path, reset their paths
        if (!pathRef.current[user]) {
            pathRef.current[user] = {
                showing: [],
                pending: []
            };
        } else if (
            (pathRef.current[user].pending.find((p) => p.terminal) ||
                pathRef.current[user].showing.find((p) => p.terminal)) &&
            !relativePoint.terminal
        ) {
            pathRef.current[user] = {
                showing: [],
                pending: []
            };
        }

        // Test presence, not truthiness: rx is normalised to the rendered
        // image, so rx === 0 is a point on its left edge, not a missing one.
        if (relativePoint.rx !== undefined || relativePoint.terminal) {
            const { rx, ry, terminal, t, renderAt } = relativePoint;
            const imageRect = getImageRect(
                imgRef.current,
                canRef.current.width,
                canRef.current.height
            );
            pathRef.current[user].pending.push(new Point(rx, ry, imageRect, terminal, t, renderAt));
        }
    };

    // Add your mouse position to your points array
    const addPoint = (x, y) => {
        // Calculate image rect to normalize coordinates relative to the rendered image
        // (not the full canvas) so they sync correctly across different screen sizes
        const imageRect = getImageRect(
            imgRef.current,
            canRef.current.width,
            canRef.current.height
        );

        // Convert canvas coordinates to image-relative coordinates
        const imageX = x - imageRect.x;
        const imageY = y - imageRect.y;

        // Normalize to 0-1 range relative to image dimensions
        const rx = imageX / imageRect.width;
        const ry = imageY / imageRect.height;
        const point = new Point(rx, ry, imageRect);

        if (MobileUtil.browser.isSafari()) {
            // Safari's performance degrades as more points are stored and makes the animation lag
            // https://stackoverflow.com/questions/45300903/canvas-drawing-takes-a-lot-of-time-on-safari-but-not-on-chrome-or-ff
            // To address this (right now) I'm going to only sample 60% of points on safari
            // Maybe this can be more intelligently fixed in the future.
            if (Math.random() < 0.6) {
                pathRef.current[uniqueDrawingId].pending.push(point);
                trailPublisher.current.addPoint({ x: rx, y: ry, t: Date.now() });
                // trailPublisher.current.pointsDone();
            }
        } else {
            pathRef.current[uniqueDrawingId].pending.push(point);
            trailPublisher.current.addPoint({ x: rx, y: ry, t: Date.now() });
            // trailPublisher.current.pointsDone();
        }
    };

    useEffect(() => {
        ctxRef.current = canRef.current.getContext('2d');
        const name = document.querySelector('#name');

        let mouseMoveEventListener;
        const getXY = (e) => {
            let x, y;
            if (
                e.type === 'touchstart' ||
                e.type === 'touchmove' ||
                e.type === 'touchend' ||
                e.type === 'touchcancel'
            ) {
                var touch = e.touches[0];
                x = touch.clientX;
                y = touch.clientY;
            } else if (
                e.type === 'mousedown' ||
                e.type === 'mouseup' ||
                e.type === 'mousemove' ||
                e.type === 'mouseover' ||
                e.type === 'mouseout' ||
                e.type === 'mouseenter' ||
                e.type === 'mouseleave'
            ) {
                x = e.clientX;
                y = e.clientY;
            }

            let rect = canRef.current.getBoundingClientRect();
            x -= rect.left;
            y -= rect.top;
            return { x, y };
        };

        if (MobileUtil.browser.isSafari()) {
            mouseMoveEventListener = (e) => {
                e.preventDefault();
                name.innerText = user.firstName;
                name.style.zIndex = 2000;
                name.style.left = `${e.clientX}px`;
                name.style.top = `${e.clientY + 30}px`;
                const { x, y } = getXY(e);
                if (buttonRef.current) {
                    addPoint(x, y);
                }
            };
        } else {
            mouseMoveEventListener = (e) => {
                e.preventDefault();
                name.innerText = user.firstName;
                name.style.zIndex = 2000;
                name.style.left = `${e.clientX}px`;
                name.style.top = `${e.clientY + 30}px`;
                const { x, y } = getXY(e);
                if (buttonRef.current) {
                    addPoint(x, y);
                }
            };
        }

        EventManager.reactPathEvent((e) => {
            const msg = JSON.parse(e.data);

            if (document.visibilityState !== 'visible') {
                // don't add points if the user is not looking at the page ("latent drawing")
                return;
            }

            if (msg.roomId != props.roomId || msg.uniqueDrawingId == uniqueDrawingId) {
                // If the user is logged into multiple rooms at once, only draw
                // the points for the room they belong to
                return;
            }

            const trail = msg.points;
            trail.sort((a, b) => a.t - b.t);
            const batchReceiveTime = Date.now();
            const minT = trail[0]?.t ?? batchReceiveTime;
            trail.forEach(({ x, y, terminal, t }) => {
                // Schedule each point to render at the same relative interval it was drawn,
                // replaying the original cadence starting from now rather than all at once
                const renderAt = batchReceiveTime + ((t ?? batchReceiveTime) - minT);
                addRelativePoint(msg.uniqueDrawingId, { rx: x, ry: y, terminal, t, renderAt });
            });
        });

        const stopStreaming = () => {
            if (streamingIntervalRef.current) {
                clearInterval(streamingIntervalRef.current);
                streamingIntervalRef.current = null;
            }
        };

        const mouseDownEventListener = (e) => {
            pathRef.current[uniqueDrawingId] = { showing: [], pending: [] };
            buttonRef.current = true;
            stopStreaming();
            streamingIntervalRef.current = setInterval(() => {
                trailPublisher.current.flushIntermediate();
            }, 100);
        };

        const mouseLeaveEventListener = (e) => {
            name.innerText = '';
            stopStreaming();
            trailPublisher.current.pointsDone();
        };

        const mouseUpEventListener = (e) => {
            buttonRef.current = false;
            stopStreaming();
            trailPublisher.current.pointsDone();
        };

        if (!props.readOnly) {
            canRef.current.addEventListener('mousemove', mouseMoveEventListener, false);
            canRef.current.addEventListener('touchmove', mouseMoveEventListener, { passive: false });
            canRef.current.addEventListener('mousedown', mouseDownEventListener);
            canRef.current.addEventListener('touchstart', mouseDownEventListener);
            canRef.current.addEventListener('mouseup', mouseUpEventListener);
            canRef.current.addEventListener('touchend', mouseLeaveEventListener);
            canRef.current.addEventListener('mouseleave', mouseLeaveEventListener);
        }

        return () => {
            canRef.current?.removeEventListener('mousemove', mouseMoveEventListener);
            canRef.current?.removeEventListener('touchmove', mouseMoveEventListener);
            canRef.current?.removeEventListener('mousedown', mouseDownEventListener);
            canRef.current?.removeEventListener('touchstart', mouseDownEventListener);
            canRef.current?.removeEventListener('mouseup', mouseUpEventListener);
            canRef.current?.removeEventListener('touchend', mouseLeaveEventListener);
            canRef.current?.removeEventListener('mouseleave', mouseLeaveEventListener);
            if (streamingIntervalRef.current) clearInterval(streamingIntervalRef.current);
            name.innerText = '';
            EventManager.unsetPathEvent();
        };
    }, [canRef, props.readOnly]);

    const startAnimation = () => {
        const FPS = 16; // 16 -> 60fps, 22 -> 45fps
        const animatePoints = (time = 0) => {
            const elapsed = time - timeRef.current;
            // if enough time has passed to warrant the next frame, animate. (60fps)
            if (elapsed >= FPS) {
                ctxRef.current.clearRect(
                    0,
                    0,
                    ctxRef.current.canvas.width,
                    ctxRef.current.canvas.height
                );
                timeRef.current = time - (elapsed % FPS);
                const usernames = Object.keys(pathRef.current);

                for (let j = 0; j < usernames.length; j++) {
                    const userId = usernames[j];

                    const now = Date.now();
                    while (
                        pathRef.current[userId].pending.length &&
                        now >= pathRef.current[userId].pending[0].renderAt
                    ) {
                        const point = pathRef.current[userId].pending.shift();
                        // Age the fade to reflect how old the point actually is at render time
                        if (point.t) {
                            point.remainingLife = Math.max(
                                0,
                                100 - (now - point.t) / MS_PER_FRAME
                            );
                        }
                        pathRef.current[userId].showing.push(point);
                    }

                    if (pathRef.current[userId].showing.length) {
                        for (let i = 0; i < pathRef.current[userId].showing.length; ++i) {
                            let [head] = pathRef.current[userId].showing.slice(-1);

                            // When sending a trail over the event, there is a "terminal" point
                            // which acts like a null-terminated character. It carries no
                            // coordinates, so it projects to NaN - step back to the last
                            // point that has a real position. A trail that is only a
                            // terminal point has nothing to step back to: slice(-2) returns
                            // that same point again. That is why the pointer draw below is
                            // guarded rather than assumed safe - drawing at NaN is a silent
                            // no-op on a canvas, so it would otherwise just vanish.
                            if (!hasPosition(head)) {
                                [head] = pathRef.current[userId].showing.slice(-2);
                            }
                            const point = pathRef.current[userId].showing[i];
                            let lastPoint;

                            if (pathRef.current[userId].showing[i - 1] !== undefined) {
                                lastPoint = pathRef.current[userId].showing[i - 1];
                            } else {
                                lastPoint = point;
                            }
                            point.decrement();

                            if (point.remainingLife <= 0) {
                                // If the point dies, remove it.
                                pathRef.current[userId].showing.shift();
                            } else {
                                // //DR test code.
                                // let point1 = new HardPoint(0, 0);
                                // let point2 = new HardPoint(10, 10);
                                // drawThinningTrail(ctxRef.current, point1, point2);
                                // let point3 = new HardPoint(
                                //     ctxRef.current.canvas.width,
                                //     ctxRef.current.canvas.height
                                // );
                                // let point4 = new HardPoint(
                                //     ctxRef.current.canvas.width - 10,
                                //     ctxRef.current.canvas.height - 10
                                // );
                                // drawThinningTrail(ctxRef.current, point3, point4);

                                // console.log('drawing thinning trail', point, lastPoint);
                                // console.log('head', head);
                                drawThinningTrail(ctxRef.current, point, lastPoint);
                                if (userId !== uniqueDrawingId && hasPosition(head)) {
                                    drawPointer(
                                        ctxRef.current,
                                        head,
                                        userId.split('_')[2] || 'something'
                                    );
                                }
                            }
                        }
                    }
                }
            }
            animationRef.current = requestAnimationFrame((time) => animatePoints(time));
        };

        animatePoints();
    };

    // A re-fit - or a new image - moves the image under the strokes, so every
    // stored point has to be derived again from its image-relative
    // coordinates. The animation loop repaints from these arrays each frame,
    // so it picks the new positions up on its own - nothing needs redrawing
    // here.
    const reprojectPoints = () => {
        const imageRect = getImageRect(img, canRef.current.width, canRef.current.height);

        Object.values(pathRef.current).forEach(({ showing, pending }) => {
            showing.forEach((point) => point.project(imageRect));
            pending.forEach((point) => point.project(imageRect));
        });
    };

    const resize = () => {
        if (!img) return;

        // Only a re-fit invalidates the context, but the image rect also moves
        // when the image itself changes - a new slide with a different aspect
        // ratio letterboxes differently in a container that never resized - so
        // reproject either way.
        if (fitCanvasToParent(canRef.current)) {
            ctxRef.current = canRef.current.getContext('2d');
        }
        reprojectPoints();
    };

    const update = () => {
        resize();
    };

    // Watch the container, not the window: dragging the chat pane divider
    // resizes this canvas' parent without firing a window resize, which used to
    // leave the drawing layer on a stale bitmap while the image layer grew -
    // strokes then stopped at the old bottom edge (#209).
    useEffect(() => {
        if (!img) return undefined;

        update();
        return observeSize(canRef.current.parentElement, update);
    }, [img]);

    useEffect(() => {
        startAnimation();
        return () => cancelAnimationFrame(animationRef.current);
    }, [props.img]);

    return (
        <canvas
            style={{
                position: 'absolute',
                zIndex: '5', // to make this canvas always above the other canvas
                cursor: props.cursor
                // border: '1px solid black'
            }}
            ref={canRef}
        />
    );
}

export default Canvas;
